import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { SIDEBAR_WIDTH } from '../../../constants';
import { useGetIntegrationUsersQuery } from '../../../services';
import { ComponentSize, ExternalCallType, IntegrationService, TextColor } from '../../../types';
import {
  ButtonColor,
  ButtonVariant,
  DatePickerRange,
  Icon,
  Icons,
  SidebarDateFilter,
  SidebarSelectFilter,
  TextButton,
  TextInput,
  Toggle,
  Typography,
  TypographyType,
} from '../../shared';

interface IntegrationCallsFiltersProps {
  service: IntegrationService;
  selectedParticipants: string[];
  setSelectedParticipants: (participants: string[]) => void;
  setSelectedDate: Dispatch<SetStateAction<DatePickerRange | undefined>>;
  setSearchCallId: Dispatch<SetStateAction<string | undefined>>;
  setSearchError: Dispatch<SetStateAction<string>>;
  setSelectedCallType: Dispatch<SetStateAction<ExternalCallType | undefined>>;
  setSelectedCallIds: Dispatch<SetStateAction<string[]>>;
  selectedDate?: DatePickerRange;
  selectedCallType?: ExternalCallType;
  searchCallId?: string;
  searchError?: string;
}

const IntegrationCallsFilters = ({
  service,
  selectedParticipants,
  setSelectedParticipants,
  selectedDate,
  setSelectedDate,
  selectedCallType,
  setSelectedCallType,
  searchCallId,
  setSearchCallId,
  searchError,
  setSearchError,
  setSelectedCallIds,
}: IntegrationCallsFiltersProps) => {
  const { data: integrationUsers, isLoading: isLoadingIntegrationUsers } = useGetIntegrationUsersQuery({
    service,
  });

  // Memoized options for the participants filter list
  const participantOptions = useMemo(() => {
    if (!integrationUsers?.users) return [];

    return integrationUsers.users.map((user) => ({
      value: user.id,
      label: `${user.firstName} ${user.lastName}`,
    }));
  }, [integrationUsers]);

  // Handles clearing the filters
  const handleClearFilters = () => {
    setSelectedParticipants([]);
    setSelectedDate(undefined);
    setSearchCallId(undefined);
    setSearchError('');
  };

  const handleChangeCallType = useCallback(
    (callType: ExternalCallType) => {
      setSelectedCallType(callType);
      setSelectedCallIds([]);
      handleClearFilters();
    },
    [setSelectedCallType, setSelectedCallIds, handleClearFilters]
  );

  const handleChangeSearchCallId = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchError('');
      setSearchCallId(event.target.value);
    },
    [setSearchError, setSearchCallId]
  );

  const hasFilters = selectedParticipants.length > 0 || selectedDate || searchCallId;
  // Call ID filter is not available for salesloft phone calls
  const hideCallIdFilter = selectedCallType === ExternalCallType.PHONE_CALL;
  const isSalesLoft = service === IntegrationService.SALESLOFT;

  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-hidden p-6" style={{ width: SIDEBAR_WIDTH }}>
      <Typography type={TypographyType.H3} className="sticky top-0">
        Filters
      </Typography>

      <div className="flex flex-col gap-2">
        {isSalesLoft && (
          <div className="mb-4">
            <Toggle
              leftLabel={
                <Typography color={TextColor.SECONDARY} noWrap>
                  Phone calls
                </Typography>
              }
              rightLabel={
                <Typography color={TextColor.SECONDARY} noWrap>
                  Video calls
                </Typography>
              }
              size={ComponentSize.MEDIUM}
              checked={selectedCallType === ExternalCallType.CONVERSATION}
              onChange={(checked) =>
                handleChangeCallType(checked ? ExternalCallType.CONVERSATION : ExternalCallType.PHONE_CALL)
              }
            />
          </div>
        )}

        {!hideCallIdFilter && (
          <div className="flex flex-col gap-2">
            <Typography color={TextColor.SECONDARY}>Search by call ID</Typography>
            <TextInput
              placeholder="Call ID or link"
              value={searchCallId}
              error={searchError}
              endElement={<Icons icon={Icon.SEARCH} className="pr-1" color={TextColor.SECONDARY} />}
              onChange={handleChangeSearchCallId}
            />
          </div>
        )}

        <div className="flex flex-col gap-6">
          <SidebarSelectFilter
            placeholder="Type to filter"
            title="Call participants"
            options={participantOptions}
            onChange={(selected) => setSelectedParticipants(selected || [])}
            loading={isLoadingIntegrationUsers}
            selected={selectedParticipants}
          />

          <SidebarDateFilter title="Call date" onChange={setSelectedDate} selected={selectedDate} />

          {hasFilters && (
            <TextButton
              text="Clear filters"
              onClick={handleClearFilters}
              variant={ButtonVariant.SOFT}
              fullWidth
              color={ButtonColor.SECONDARY}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default IntegrationCallsFilters;
